import { useApiIsLoaded, useMapsLibrary } from '@vis.gl/react-google-maps';
import React from 'react';
import { SelectedPlace } from 'actions/onlineBooking/actions/OnlineBookingWalkingAddress';
import { useDebounce } from 'use-debounce';

export interface AddressSuggestion {
    id: string;
    label: string;
    prediction: google.maps.places.PlacePrediction;
}

export interface AddressComponentLike {
    types: Array<string>;
    longText?: string | null;
    shortText?: string | null;
}

const CITY_COMPONENT_TYPES = [
    'locality',
    'sublocality_level_1',
    'postal_town',
    'administrative_area_level_3',
    'neighborhood',
    'administrative_area_level_2'
] as const;

const componentText = (
    components: Array<AddressComponentLike> | undefined,
    type: string,
    short?: boolean
): string => {
    const match = components?.find(component => component.types.includes(type));

    if (!match) {
        return '';
    }

    return (short ? match.shortText : match.longText) ?? '';
};

export const cityFromAddressComponents = (
    components: Array<AddressComponentLike> | undefined
): string => {
    for (const type of CITY_COMPONENT_TYPES) {
        const value = componentText(components, type);

        if (value) {
            return value;
        }
    }

    return '';
};

export interface GeocodeResultLike {
    address_components?: Array<{
        types: Array<string>;
        long_name?: string;
        short_name?: string;
    }>;
}

const geocodeComponentText = (
    components: GeocodeResultLike['address_components'],
    type: string
): string => {
    const match = components?.find(component => component.types.includes(type));

    return match?.long_name ?? match?.short_name ?? '';
};

export const postalCodeFromGeocodeResults = (results: Array<GeocodeResultLike>): string =>
    geocodeComponentText(results[0]?.address_components, 'postal_code');

const localityFromGeocodeResults = (results: Array<GeocodeResultLike>): string =>
    geocodeComponentText(results[0]?.address_components, 'locality');

export const toSelectedPlace = (
    suggestion: Pick<AddressSuggestion, 'label'>,
    place: {
        location?: { lat: () => number; lng: () => number } | null;
        formattedAddress?: string | null;
        addressComponents?: Array<AddressComponentLike>;
    }
): SelectedPlace | undefined => {
    const location = place.location;

    if (!location) {
        return undefined;
    }

    const components = place.addressComponents;
    const city = cityFromAddressComponents(components);

    return {
        formattedAddress: place.formattedAddress || suggestion.label,
        streetNumber: componentText(components, 'street_number'),
        route: componentText(components, 'route'),
        city,
        state: componentText(components, 'administrative_area_level_1', true),
        zipcode: componentText(components, 'postal_code'),
        country: componentText(components, 'country', true),
        latitude: location.lat(),
        longitude: location.lng(),
        subpremise: componentText(components, 'subpremise')
    };
};

export const usePlacesAutocomplete = (input: string, regionCode?: string) => {
    const places = useMapsLibrary('places');
    const apiLoaded = useApiIsLoaded();
    const [suggestions, setSuggestions] = React.useState<Array<AddressSuggestion>>([]);
    const [ready, setReady] = React.useState(false);
    const [timedOut, setTimedOut] = React.useState(false);
    const sessionTokenRef = React.useRef<google.maps.places.AutocompleteSessionToken>();
    const skipQueryRef = React.useRef<string>();
    const [debouncedInput] = useDebounce(input, 300);

    React.useEffect(() => {
        if (places) {
            setTimedOut(false);
            return;
        }

        const timeoutId = window.setTimeout(() => setTimedOut(true), 5000);

        return () => window.clearTimeout(timeoutId);
    }, [places]);

    React.useEffect(() => {
        const query = debouncedInput.trim();

        if (!places || !query || query === skipQueryRef.current) {
            setSuggestions([]);
            setReady(false);
            return;
        }

        let cancelled = false;
        setReady(false);

        const search = async () => {
            if (!sessionTokenRef.current) {
                sessionTokenRef.current = new places.AutocompleteSessionToken();
            }

            const request: google.maps.places.AutocompleteRequest = {
                input: query,
                sessionToken: sessionTokenRef.current,
                includedPrimaryTypes: ['street_address', 'premise', 'subpremise']
            };

            if (regionCode) {
                request.includedRegionCodes = [regionCode];
            }

            const { suggestions: results } =
                await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

            if (cancelled) return;

            setSuggestions(
                (results ?? [])
                    .map(result => result.placePrediction)
                    .filter(
                        (prediction): prediction is google.maps.places.PlacePrediction =>
                            !!prediction
                    )
                    .map(prediction => ({
                        id: prediction.placeId,
                        label: prediction.text.toString(),
                        prediction
                    }))
            );
            setReady(true);
        };

        search().catch(() => {
            if (cancelled) return;
            setSuggestions([]);
            setReady(true);
        });

        return () => {
            cancelled = true;
        };
    }, [places, debouncedInput, regionCode]);

    const selectSuggestion = async (
        suggestion: AddressSuggestion
    ): Promise<SelectedPlace | undefined> => {
        const place = suggestion.prediction.toPlace();
        await place.fetchFields({
            fields: ['location', 'formattedAddress', 'addressComponents']
        });
        sessionTokenRef.current = undefined;
        skipQueryRef.current = suggestion.label;
        setSuggestions([]);

        const selected = toSelectedPlace(suggestion, place);

        if (!selected || selected.zipcode || !place.location) {
            return selected;
        }

        try {
            const { results } = await new google.maps.Geocoder().geocode({
                location: place.location
            });
            const postalCode = postalCodeFromGeocodeResults(results);

            return {
                ...selected,
                zipcode: postalCode,
                city: selected.city || localityFromGeocodeResults(results)
            };
        } catch {
            return selected;
        }
    };

    const clearSkip = () => {
        skipQueryRef.current = undefined;
    };

    const placesUnavailable = !places && (apiLoaded || timedOut);

    return { suggestions, ready, selectSuggestion, clearSkip, placesUnavailable };
};
